import * as PrimitiveRadixDialog from '@radix-ui/react-dialog';
import { ReactNode, Ref, useCallback, useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import { SheetContext, SheetContextValue } from './_sheet.context';
import { ImperativeSheetRef } from './sheet.component';

export function SheetRoot(
	{
		label = null,
		variant = 'full',
		onOpenChange,
		onOpen,
		onClose,
		children,
		defaultOpen = false,
		open: controlledOpen,
		onOpenAutoFocus,
		onCloseAutoFocus,
		onEscapeKeyDown,
		onPointerDownOutside,
		onInteractOutside,
		onCloseAnimationEnd,
	}: SheetRootProps,
	ref: Ref<ImperativeSheetRef>,
): JSX.Element {
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const [isOpen, setIsOpen] = useState(defaultOpen);
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const id = useId();
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const hasBlockedOpenCloseCallbacksOnce = useRef(false);

	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const triggerOpenCloseCallbacks = useCallback(
		function (open: boolean): void {
			if (open === true) {
				onOpen?.();
			} else {
				onClose?.();
			}
		},
		[onOpen, onClose],
	);

	function handleOpenChange(open: boolean): void {
		if (controlledOpen === undefined) {
			setIsOpen(open);
			triggerOpenCloseCallbacks(open);
		}

		onOpenChange?.(open);
	}

	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	useEffect(
		function whenControlledIsOpenChanges() {
			if (controlledOpen === undefined) {
				hasBlockedOpenCloseCallbacksOnce.current = true;

				return;
			}

			setIsOpen(controlledOpen);

			if (hasBlockedOpenCloseCallbacksOnce.current === false) {
				hasBlockedOpenCloseCallbacksOnce.current = true;

				return;
			}

			triggerOpenCloseCallbacks(controlledOpen);
		},
		[controlledOpen, triggerOpenCloseCallbacks],
	);

	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	useImperativeHandle(ref, () => ({
		open: () => {
			handleOpenChange(true);
		},
		close: () => {
			handleOpenChange(false);
		},
	}));

	return (
		<SheetContext.Provider
			value={{
				id,
				isOpen,
				setIsOpen: handleOpenChange,
				label,
				variant,
				onOpenAutoFocus,
				onCloseAutoFocus,
				onEscapeKeyDown,
				onPointerDownOutside,
				onInteractOutside,
				onCloseAnimationEnd,
			}}
		>
			<PrimitiveRadixDialog.Root open={isOpen} onOpenChange={handleOpenChange}>
				{children}
			</PrimitiveRadixDialog.Root>
		</SheetContext.Provider>
	);
}

export interface SheetRootProps {
	children: ReactNode;

	/**
	 * @default null
	 */
	label?: ReactNode;

	/**
	 * @default full
	 */
	variant?: SheetContextValue['variant'];
	onOpenChange?: (value: boolean) => void;
	onOpen?: () => void;
	onClose?: () => void;
	defaultOpen?: boolean;
	open?: boolean;
	onCloseAnimationEnd?: () => void;
	onOpenAutoFocus?: PrimitiveRadixDialog.DialogContentProps['onOpenAutoFocus'];
	onCloseAutoFocus?: PrimitiveRadixDialog.DialogContentProps['onCloseAutoFocus'];
	onEscapeKeyDown?: PrimitiveRadixDialog.DialogContentProps['onEscapeKeyDown'];
	onPointerDownOutside?: PrimitiveRadixDialog.DialogContentProps['onPointerDownOutside'];
	onInteractOutside?: PrimitiveRadixDialog.DialogContentProps['onInteractOutside'];
}
